<script setup lang="ts">
import AddAddress from "~/components/component/Address/AddAddress.vue";
import SelectAddressWithActions from "~/components/component/Address/SelectAddressWithActions.vue";
import type {Address} from "~/composables/store/user/address/interface";
import EditAddress from "~/components/component/Address/EditAddress.vue";
import { updateFormattingAddress} from "~/composables/api/profile/address";
import type {AddressErrors} from "~/types/addressTypes";
import {updateStoreAddress, useStoreSelectedAddress} from "~/composables/store/user/address/store";
import {fetchAndSetAddresses, selectAddressWithChangeState} from "~/composables/state/address/store";

const {$toast,$toastError} = useNuxtApp()
const route = useRoute()
const isBccPage = computed(() => route.name === 'bcc')
const addresses = useStoreAddresses()
const selectedAddress = useStoreSelectedAddress()
const clickedNewAddress = ref(false)
const clickedEditAddress = ref(false)
const selectedEditAddress = ref<Address|null>(null)
const errorsEditAddress = ref(<AddressErrors>{
  city: {
    isError: false,
    text: ''
  },
  street: {
    isError: false,
    text: ''
  },
  house: {
    isError: false,
    text: ''
  },
  isError: false
})
const isOkNext = ref(false)
const addAddressRef = ref(null)
const emit = defineEmits(['back'])
const props = defineProps({
  showSaveButton: {
    type: Boolean,
    default: true
  }
})
watch(addresses, async (value) => {
  if (value.length === 1 && !selectedAddress.value) {
    handleSelectAddress(value[0])
  }
})

const ctaDisabled = computed(() => (clickedNewAddress.value || !addresses.value.length) ? !!addAddressRef.value?.ctaDisabled : false)
const isSubView = computed(() => clickedNewAddress.value || clickedEditAddress.value)

function exitSubView() {
  clickedNewAddress.value = false
  clickedEditAddress.value = false
}

defineExpose({onNext, ctaDisabled, isSubView, exitSubView})
async function addAddress() {
  const {address, response} = await addAddressRef.value.handleAddAddress()
  handleSelectAddress(address)
  isOkNext.value = !!response
  return !!isOkNext.value
}

async function editAddress() {
  isOkNext.value = await handleUpdateAddress(selectedEditAddress.value)
  handleSelectAddress(selectedEditAddress.value)
  return isOkNext.value
}

function onNext() {
  if (clickedEditAddress.value) {
    return editAddress()
  } else if (clickedNewAddress.value || !addresses.value.length) {
    return addAddress()
  } else {
    return true
  }
}
function handleClickedNewAddress() {
  clickedNewAddress.value =  !clickedNewAddress.value
}

function handleSelectAddress(address:Address){
  selectAddressWithChangeState(address)
}

function handleEditAddress(address:Address){
  clickedEditAddress.value=true
  selectedEditAddress.value=address
}

async function handleDelete(addressId:string){
  const {data:response,error,pending} = await deleteAddress(addressId)
  if (response.value) {
    removeSelectedAddress(addressId)
    const responseData = response.value as {message:string}
    $toast(responseData.message)
  }
  if (error.value) {
    console.error('Error in getting profile', error)
  }
  return {response, error}
}

function removeSelectedAddress(addressId:string){
  removeStoreAddress(addressId)
}

function handleBackEdit(){
  clickedEditAddress.value=false
}
async function handleUpdateAddress(address: Address) {
  if (!selectedEditAddress.value){
    return false
  }
  errorsEditAddress.value = useValidateAddress(address, isBccPage.value)
  if (errorsEditAddress.value.isError) {
    return false
  }
  const {data: response, error} = await updateFormattingAddress(address)
  if (response.value) {
    updateStoreAddress(address)
    clickedEditAddress.value = false
    const responseMessage= response.value as {message:string}
    $toast(responseMessage.message)
    return true
  }
  if (error.value) {
    console.error('Error in getting profile', error)
    return false
  }
}
async function handleFetch()
{
  await fetchAndSetAddresses()
}
</script>

<template>
  <div id="address-manager">
    <template v-if="addresses.length>0">
      <SelectAddressWithActions
          v-if="!clickedNewAddress && !clickedEditAddress"
          :addresses="addresses"
          :selected-address="selectedAddress"
          @handleEdit="handleEditAddress"
          @handleDelete="handleDelete"
          @handleSelect="handleSelectAddress"
      />
      <button
          v-if="!clickedNewAddress && !clickedEditAddress && isBccPage"
          type="button"
          @click="handleClickedNewAddress"
          class="mt-3 h-11 px-4 rounded-[13px] border-[1.5px] border-dashed border-[#cdd6d0] bg-white text-primary-green text-sm font-bold w-fit"
      >+ {{ $t('input_forms.button.add_new_address') }}</button>
      <UIBtn
          @click="handleClickedNewAddress"
          v-if="!clickedNewAddress && !clickedEditAddress && !isBccPage" :border="'green'"
          class="mt-2 rounded-full w-fit">{{
          $t('input_forms.button.add_new_address')
        }}
      </UIBtn>
      <AddAddress
          v-if="clickedNewAddress && !clickedEditAddress"
          :showSaveButton
          :show-back-button="!isBccPage"
          ref="addAddressRef"
          @back="handleClickedNewAddress"
          @next="handleClickedNewAddress"
      />
      <EditAddress
          v-if="clickedEditAddress && selectedEditAddress"
          @back="handleBackEdit"
          @update="handleUpdateAddress"
          v-model="selectedEditAddress"
          :errors="errorsEditAddress"
          :showSaveButton
          :show-back-button="!isBccPage"
      />
    </template>
    <template v-else>
      <AddAddress
          ref="addAddressRef"
          :show-save-button="showSaveButton"
          :show-back-button="false"
          @next="handleFetch"
      />
    </template>
  </div>
</template>

<style scoped>

</style>